// src/utils/sessionClock.js
//
// The clock follows the session: signing in starts it, closing the browser
// stops it.
//
// The button in the header is a readout now — every punch is made by something
// the person actually did. This file owns the two ends of the session; the
// work-status menu and the task board own the rest.

import { apiPath } from "./apiPath";

/* Guards resumeSessionClock against React 18 StrictMode mounting the effect
   twice. Module scope on purpose: it is born false on every page load and
   dies with the page, which is exactly the lifetime wanted.

   This was sessionStorage, and that is a different lifetime — sessionStorage
   survives a refresh and only clears when the TAB closes. So the first load in
   a tab clocked in and set the key; every refresh after that sent the
   `pagehide` clock-out and then found the key still there and returned early,
   never clocking back in. The person was left clocked out by a refresh, with
   the header button, the working-hours timer and the Clock Status panel all
   correctly reporting a state nobody had chosen. It reads as "the Active
   button does nothing", which is how it was found. */
let resumedThisPageLoad = false;

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}") || {};
  } catch {
    return {};
  }
};

/* Fire-and-forget, and it has to survive the page being torn down.
   `navigator.sendBeacon` is the only request a browser guarantees to finish
   once a tab is closing — a fetch is cancelled with the page, and
   `beforeunload` cannot await anything. It sends a Blob rather than JSON
   because a beacon cannot set Content-Type on its own. */
const beacon = (url, payload) => {
  try {
    const body = new Blob([JSON.stringify(payload)], {
      type: "application/json",
    });
    if (navigator.sendBeacon?.(url, body)) return true;
  } catch {
    /* fall through */
  }

  /* Safari has historically dropped beacons on pagehide. keepalive on fetch is
     the documented fallback and behaves the same way. */
  try {
    fetch(url, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
      keepalive: true,
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * Tell the server the session is over, so it can clock the person out.
 *
 * `reason` separates a deliberate Log out from the browser being closed; both
 * end the session, but only one of them is the person saying so.
 */
export const endSessionBeacon = (reason = "closed") => {
  const user = readUser();
  if (!user?.userId) return;

  beacon(`${apiPath.API_URL}/auth/session-end`, {
    userId: user.userId,
    reason,
  });
};

/**
 * Start listening for the tab going away. Returns the teardown.
 *
 * `pagehide` rather than `beforeunload`: it fires on mobile Safari and on tabs
 * discarded to save memory, where beforeunload does not, and it is the event
 * browsers still allow a beacon from.
 *
 * It also fires on a REFRESH and on navigating away, which are not somebody
 * leaving for the day. Nothing in the browser can tell those apart at the
 * moment they happen — the difference only shows in whether the app comes
 * back. So the clock-out is sent either way and `resumeSessionClock` below
 * puts a returning session straight back on the clock: a refresh nets out to a
 * clock-out and a clock-in a second apart, rather than an afternoon of
 * attendance quietly missing.
 */
export const watchSessionEnd = () => {
  const onHide = (e) => {
    /* `persisted` means the page went into the back/forward cache and is
       expected back — the tab is not closing, so the session has not ended. */
    if (e.persisted) return;
    endSessionBeacon("closed");
  };

  window.addEventListener("pagehide", onHide);
  return () => window.removeEventListener("pagehide", onHide);
};

/**
 * Put a returning session back on the clock.
 *
 * Runs once when the app boots with a valid token — after a refresh, after
 * reopening a closed tab, after the browser restores a window. It is the same
 * punch signing in makes, and the server answers ALREADY when the person is
 * still clocked in, so calling it more often than needed costs nothing.
 */
export const resumeSessionClock = async (clockIn) => {
  const user = readUser();
  if (!user?.userId) return;

  /* Once per page load, not once per mount: React 18's StrictMode mounts
     effects twice in development, and a token that is already on the clock
     does not need telling twice.

     It must NOT be once per tab. `pagehide` fires on every refresh and sends a
     clock-out, so every load has to clock back in — that is what nets a
     refresh out to an out-and-in a second apart instead of losing the rest of
     the day. The server answers ALREADY when they were never off the clock,
     so the extra call costs nothing. */
  if (resumedThisPageLoad) return;
  resumedThisPageLoad = true;

  try {
    await clockIn(user.userId);
  } catch {
    /* Never surfaced. Attendance is a side effect of using the app; failing to
       record it must not stop somebody working. */
  }
};
